import { cn } from "@/shared/lib/cn";

export interface NotificationsBadgeProps {
  /** Cuántas novedades sin leer. Con 0 no se pinta NADA. */
  count: number;
  className?: string;
}

/** A partir de aquí el número deja de caber y se resume. */
const MAX_SHOWN = 9;

/**
 * El globo con el número, en la esquina de la campana.
 *
 * ── Con cero no hay globo ────────────────────────────────────────────
 * Devuelve `null`, no un círculo vacío ni un "0". Un globo siempre
 * encendido es un aviso que no avisa: si está ahí tanto cuando hay algo
 * como cuando no, deja de mirarse en una semana y el día que sí importe
 * ya nadie lo ve. La campana sola ya dice "aquí están tus novedades".
 *
 * ── Por qué es decorativo ────────────────────────────────────────────
 * `aria-hidden`: el número YA viaja en el nombre accesible del botón
 * ("Notificaciones, 3 sin leer"), que es donde un lector de pantalla lo
 * encuentra sin tener que entrar. Sin esto lo diría dos veces.
 *
 * ── Y por qué no come clics ──────────────────────────────────────────
 * Se pinta ENCIMA del botón —es hermano suyo, no hijo: `Button` con
 * `iconOnly` no pinta `children`— así que sin `pointer-events-none` el
 * globo se tragaría el clic justo en la esquina donde más se pulsa.
 */
export function NotificationsBadge({ count, className }: NotificationsBadgeProps) {
  if (count <= 0) return null;

  return (
    <span
      aria-hidden="true"
      className={cn(
        "pointer-events-none absolute -right-1 -top-1 z-10 flex h-[18px] min-w-[18px]",
        "items-center justify-center rounded-ds-pill border border-white/70 bg-ds-violet-pop",
        "px-1 text-[10px] font-semibold leading-none text-white shadow-ds-card",
        className,
      )}
    >
      {count > MAX_SHOWN ? `${MAX_SHOWN}+` : count}
    </span>
  );
}
